import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useSubscription, AddressData } from '../../context/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { PulsatingButton } from '../ui/pulsating-button';
import AddressForm from './AddressForm';
import { getCountryName } from '../../constants/countries';

// Define interfaces for the component
interface PriceData {
  amount: number;
  currency: string;
  interval?: string;
  trial_period_days?: number;
}

// Define Product interface based on OpenAPI schema
interface Product {
  id: string;
  name: string;
  description: string;
  trial_period_days: number;
  is_active: boolean;
  created_at?: string;
}

// Define SubscriptionTier interface based on how it's used in the application
interface SubscriptionTier {
  id: string;
  product: string;  // This is just the ID
  name: string;
  description: string;
  stripe_price_id_monthly: string;
  stripe_price_id_annual: string;
  monthly_price_usd: number;
  annual_price_usd: number;
  features: string[] | Record<string, any>;
  created_at?: string;
}

interface SubscriptionModalProps {
  onClose: () => void;
  onSubscriptionCreated?: () => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fff',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#6B7280',
      },
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444',
    },
  },
  hidePostalCode: true,
};



const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ onClose, onSubscriptionCreated }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { 
    createSetupIntent, 
    createSubscription, 
    fetchSubscriptionTiers,
    fetchProduct,
    product,
    subscriptionTiers, 
    hasHadTrial, 
    checkTrialStatus 
  } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);
  const [isStartingFreeTrial, setIsStartingFreeTrial] = useState(false);
  const [address, setAddress] = useState<AddressData | null>(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [currentStep, setCurrentStep] = useState<'address' | 'payment'>('address');
  const [discountCode, setDiscountCode] = useState('');

  // Check if user is starting a free trial
  useEffect(() => {
    const startingTrial = sessionStorage.getItem('starting_free_trial') === 'true';
    setIsStartingFreeTrial(startingTrial && !hasHadTrial); // Only allow trial if user hasn't had one
    
    // Refresh trial status when component mounts by forcing a product fetch
    // This will update the hasHadTrial state via the trial_eligibility field
    checkTrialStatus();
  }, [hasHadTrial, checkTrialStatus]);

  // Fetch product and subscription tiers
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setIsLoadingTiers(true);
      
      try {
        // First fetch the product if we don't have it
        if (!product) {
          await fetchProduct();
        }
        
        // Then fetch subscription tiers if we don't have them
        if (subscriptionTiers.length === 0) {
          const tiers = await fetchSubscriptionTiers(true);
          
          if (!mounted) return;
          
          if (tiers.length > 0) {
            setSelectedTierId(tiers[0].id);
          } else {
            console.error('❌ No subscription tiers returned from API');
            setError('No subscription plans available. Please try again later or contact support.');
          }
        } else {
          // If we already have tiers, use the first one as default
          setSelectedTierId(subscriptionTiers[0].id);
        }
      } catch (err) {
        console.error('❌ Failed to load subscription data:', err);
        if (mounted) {
          setError('Unable to load subscription plans. Please try again later or contact support.');
        }
      } finally {
        if (mounted) {
          setIsLoadingTiers(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchProduct, fetchSubscriptionTiers, product, subscriptionTiers]);

  useEffect(() => {
    let mounted = true;  // Track if component is mounted

    const initializeSetupIntent = async () => {
      // Check if we already have a client secret
      if (clientSecret) {
        console.log('Setup intent already initialized, skipping...');
        return;
      }

      // Don't proceed if we're still loading tiers or if no tier is selected
      if (isLoadingTiers || !selectedTierId) {
        console.log('Waiting for subscription tiers to load before initializing setup intent...');
        return;
      }

      try {
        console.log('Creating setup intent...');
        const { clientSecret: newClientSecret } = await createSetupIntent();
        console.log('Received client secret:', newClientSecret ? 'yes' : 'no');
        
        if (!newClientSecret) {
          throw new Error('No client secret received from setup intent');
        }

        // Only set state if component is still mounted
        if (mounted) {
          setClientSecret(newClientSecret);
        }
      } catch (err) {
        console.error('Setup intent error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize payment. Please try again.');
        }
      }
    };

    initializeSetupIntent();

    // Cleanup function
    return () => {
      mounted = false;
    };
  }, [createSetupIntent, clientSecret, isLoadingTiers, selectedTierId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe not initialized:', { 
        stripeAvailable: !!stripe, 
        elementsAvailable: !!elements,
        stripeKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY
      });
      setError('Stripe has not been properly initialized');
      return;
    }

    if (!clientSecret) {
      setError('No client secret available. Please try again.');
      return;
    }

    if (!selectedTierId) {
      setError('Please select a subscription tier.');
      return;
    }

    if (!address) {
      setError('Please provide a valid address.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      console.log('Confirming card setup...');
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        console.error('Card setup error:', error);
        throw new Error(error.message);
      }

      if (!setupIntent?.payment_method) {
        throw new Error('Failed to setup payment method');
      }

      console.log('Card setup successful, creating subscription...');
      const paymentMethodId = typeof setupIntent.payment_method === 'string' 
        ? setupIntent.payment_method 
        : setupIntent.payment_method.id;

      await createSubscription(paymentMethodId, selectedTierId, address, discountCode);
      
      // Notify parent that subscription was created successfully
      if (onSubscriptionCreated) {
        onSubscriptionCreated();
      }
      
      // Close modal - the parent will handle polling
      onClose();
      
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setProcessing(false);
    }
  };

  // Get price for a tier - now uses the pricing from the subscription tier object
  const getPrice = (tierId?: string): PriceData => {
    const tier = subscriptionTiers.find(t => t.id === (tierId || selectedTierId));
    
    if (!tier) {
      // Fallback price if no tier is found (should not happen in normal operation)
      return {
        amount: 0,
        currency: 'USD',
        interval: 'month'
      };
    }
    
    // Use monthly_price_usd from the tier
    const priceData: PriceData = {
      amount: tier.monthly_price_usd,
      currency: 'USD',
      interval: 'month'
    };
    
    // Add trial period days if user hasn't had a trial and product has trial period
    if (!hasHadTrial && product?.trial_period_days) {
      priceData.trial_period_days = product.trial_period_days;
    }
    
    return priceData;
  };

  // Format price with currency
  const formatPrice = (price: PriceData): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: price.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price.amount);
  };

  // Find the selected tier for display
  const allTiers = [...subscriptionTiers];
  const selectedTier = allTiers.find(tier => tier.id === selectedTierId);
  const price = getPrice();

  // Handle address form submission
  const handleAddressSubmit = () => {
    if (isAddressValid && address) {
      setCurrentStep('payment');
      setError(null);
    }
  };

  // Handle going back to address step
  const handleBackToAddress = () => {
    setCurrentStep('address');
    setError(null);
  };

  // Handle address change
  const handleAddressChange = (newAddress: AddressData) => {
    setAddress(newAddress);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-8 overflow-y-auto flex-1">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              {currentStep === 'payment' && (
                <button
                  onClick={handleBackToAddress}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h2 className="text-2xl font-bold text-white">
                {currentStep === 'address' 
                  ? 'Billing Information' 
                  : (!hasHadTrial && isStartingFreeTrial ? 'Start Your Free Trial' : 'Subscribe to Continue')
                }
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === 'address' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
              }`}>
                {currentStep === 'payment' ? '✓' : '1'}
              </div>
              <div className={`h-0.5 w-12 ${currentStep === 'payment' ? 'bg-green-600' : 'bg-gray-600'}`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === 'payment' ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-400'
              }`}>
                2
              </div>
            </div>
          </div>

          <div className="mb-6">
            {isLoadingTiers || !product ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-gray-300">Loading subscription options...</p>
              </div>
            ) : currentStep === 'address' ? (
              // Address Step
              subscriptionTiers.length > 0 && selectedTierId ? (
                <>
                  {product && (
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                      <p className="text-gray-400">{product.description}</p>
                    </div>
                  )}
                  <p className="text-gray-300 mb-4">
                    Select your subscription plan:
                  </p>
                  <div className="space-y-3 mb-6">
                    {subscriptionTiers.map(tier => (
                      <div 
                        key={tier.id}
                        className={`p-4 rounded-lg cursor-pointer transition-all ${
                          selectedTierId === tier.id 
                            ? 'bg-blue-900 border-2 border-blue-500' 
                            : 'bg-gray-800 border-2 border-transparent hover:border-gray-600'
                        }`}
                        onClick={() => setSelectedTierId(tier.id)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-medium text-white">{tier.name}</h3>
                            <p className="text-sm text-gray-400">
                              {tier.description}
                            </p>
                            {Array.isArray(tier.features) && tier.features.length > 0 && (
                              <ul className="mt-2 text-xs text-gray-400">
                                {tier.features.map((feature, index) => (
                                  <li key={index} className="flex items-center">
                                    <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                    </svg>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-white">
                              {formatPrice({
                                amount: tier.monthly_price_usd,
                                currency: 'USD'
                              })}
                            </div>
                            <div className="text-xs text-gray-400">per month</div>
                            {!hasHadTrial && product?.trial_period_days > 0 && (
                              <div className="text-xs text-blue-400 mt-1">
                                {product.trial_period_days}-day free trial
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <AddressForm 
                    onAddressChange={handleAddressChange}
                    isValid={isAddressValid}
                    onValidationChange={setIsAddressValid}
                  />
                </>
              ) : (
                <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 text-center">
                  <svg className="w-8 h-8 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-white mb-1">Subscription Plans Unavailable</h3>
                  <p className="text-gray-300 mb-2">{error || (subscriptionTiers.length === 0 ? 'No subscription plans available.' : 'Unable to select a subscription plan.')}</p>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Retry
                  </button>
                </div>
              )
            ) : (
              // Payment Step
              subscriptionTiers.length > 0 && selectedTierId ? (
                <>
                  {/* Show selected plan summary */}
                  {selectedTier && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-6">
                      <h4 className="text-lg font-medium text-white mb-2">Selected Plan</h4>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-medium">{selectedTier.name}</p>
                          <p className="text-sm text-gray-400">{selectedTier.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-white">
                            {formatPrice({
                              amount: selectedTier.monthly_price_usd,
                              currency: 'USD'
                            })}
                          </p>
                          <p className="text-xs text-gray-400">per month</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show address summary */}
                  {address && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-6">
                      <h4 className="text-lg font-medium text-white mb-2">Billing Address</h4>
                      <div className="text-sm text-gray-300">
                        <p>{address.address_line1}</p>
                        {address.address_line2 && <p>{address.address_line2}</p>}
                        <p>{address.city}, {address.state} {address.postal_code}</p>
                        <p>{getCountryName(address.country)}</p>
                      </div>
                    </div>
                  )}

                  <p className="text-gray-300 mb-2">
                    Enter your card details {!hasHadTrial ? 'to start your free trial' : 'to subscribe'}
                  </p>
                  <p className="text-sm text-gray-400 mb-4">
                    {!hasHadTrial ? (
                      <>
                        You won't be charged during the trial period
                        {product?.trial_period_days && ` (${product.trial_period_days} days)`}.
                        After the trial, you'll be billed {formatPrice(price)}/mo.
                      </>
                    ) : (
                      <>
                        You'll be billed {formatPrice(price)}/mo after confirming.
                      </>
                    )}
                  </p>
                </>
              ) : (
                <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 text-center">
                  <svg className="w-8 h-8 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-white mb-1">Subscription Plans Unavailable</h3>
                  <p className="text-gray-300 mb-2">{error || (subscriptionTiers.length === 0 ? 'No subscription plans available.' : 'Unable to select a subscription plan.')}</p>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Retry
                  </button>
                </div>
              )
            )}
          </div>

          {currentStep === 'address' ? (
            // Address step - show continue button
            <div>
              {error && (
                <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}
              <div className="mt-6">
                <PulsatingButton
                  onClick={handleAddressSubmit}
                  disabled={!isAddressValid || !selectedTierId || subscriptionTiers.length === 0}
                  style={{
                    width: '100%',
                    opacity: (!isAddressValid || !selectedTierId || subscriptionTiers.length === 0) ? 0.7 : 1,
                    cursor: (!isAddressValid || !selectedTierId || subscriptionTiers.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Continue to Payment
                </PulsatingButton>
              </div>
            </div>
          ) : (
            // Payment step - show card form and submit
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="discount_code" className="block text-sm font-semibold text-gray-300 mb-2">
                  Discount Code <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  id="discount_code"
                  name="discount_code"
                  type="text"
                  value={discountCode}
                  onChange={(event) => setDiscountCode(event.target.value)}
                  placeholder="Enter discount code"
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white 
                           placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                           transition-all duration-200 hover:border-gray-500 text-base"
                />
              </div>

              <div className="mb-6">
                <div className="bg-gray-800 rounded-lg p-4">
                  <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}

              <div className="mt-6">
                <PulsatingButton
                  type="submit"
                  disabled={!stripe || processing || !selectedTierId || subscriptionTiers.length === 0 || !address}
                  style={{
                    width: '100%',
                    opacity: (!stripe || processing || !selectedTierId || subscriptionTiers.length === 0 || !address) ? 0.7 : 1,
                    cursor: (!stripe || processing || !selectedTierId || subscriptionTiers.length === 0 || !address) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {processing ? 'Processing...' : (!hasHadTrial && isStartingFreeTrial) ? 'Start Free Trial' : 'Subscribe Now'}
                </PulsatingButton>
              </div>
            </form>
          )}

          <p className="text-gray-400 text-sm text-center mt-6">
            {(!hasHadTrial && isStartingFreeTrial) 
              ? 'By starting your trial, you agree to our Terms of Service and Privacy Policy'
              : 'By subscribing, you agree to our Terms of Service and Privacy Policy'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
